import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  NOTES, UPLOAD_NOTE, DELETE_NOTE, SAVE_NOTE, INCREMENT_DOWNLOAD
} from '../graphql';
import {
  BookOpen, Upload, Search, Filter, Bookmark, BookmarkCheck,
  Download, Trash2, ChevronLeft, ChevronRight, X, Plus, Minus,
  Eye, FileText, PenLine, Image, BookCopy,
  FileQuestion, LayoutGrid, List, SlidersHorizontal
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const NOTE_TYPES = [
  { value: 'HANDWRITTEN',    label: 'Handwritten',  icon: PenLine,       color: 'text-blue-600 bg-blue-50' },
  { value: 'TYPED',          label: 'Typed',         icon: FileText,      color: 'text-green-600 bg-green-50' },
  { value: 'BOOK_EXTRACT',   label: 'Book Extract',  icon: BookCopy,      color: 'text-purple-600 bg-purple-50' },
  { value: 'PREVIOUS_PAPER', label: 'Prev. Paper',   icon: FileQuestion,  color: 'text-red-600 bg-red-50' },
  { value: 'DIAGRAM',        label: 'Diagram',       icon: Image,         color: 'text-orange-600 bg-orange-50' },
  { value: 'OTHER',          label: 'Other',         icon: BookOpen,      color: 'text-gray-600 bg-gray-50' },
];

const SEMESTERS = [1, 2, 3, 4, 5, 6, 7, 8];

function noteTypeInfo(type) {
  return NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[NOTE_TYPES.length - 1];
}

// ── Full-Screen Viewer ──────────────────────────────────────────────────────
function Viewer({ note, onClose }) {
  const [page, setPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [incrementDownload] = useMutation(INCREMENT_DOWNLOAD);

  const pages = note.pages || [];
  const current = pages[page];

  const handleDownload = async () => {
    await incrementDownload({ variables: { noteId: note.id } }).catch(() => {});
    window.open(current?.imageUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div>
          <p className="text-white font-semibold text-sm">{note.title}</p>
          <p className="text-white/50 text-xs">
            {note.subject} • Sem {note.semester}
            {note.chapter ? ` • ${note.chapter}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} className="text-white/70 hover:text-white p-1 rounded">
            <Minus size={16} />
          </button>
          <span className="text-white/70 text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} className="text-white/70 hover:text-white p-1 rounded">
            <Plus size={16} />
          </button>
          <button onClick={handleDownload} className="text-white/70 hover:text-white p-1 rounded ml-2" title="Open in new tab">
            <Download size={16} />
          </button>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1 rounded ml-2">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 overflow-auto flex items-start justify-center p-4">
        {current ? (
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'center top', transition: 'transform 0.2s' }}>
            <img
              src={current.imageUrl}
              alt={`Page ${page + 1}`}
              className="max-w-full rounded shadow-2xl"
              style={{ maxHeight: '75vh' }}
              onError={e => { e.target.src = 'https://placehold.co/800x1100?text=Image+not+found'; }}
            />
            {current.caption && (
              <p className="text-white/60 text-xs text-center mt-2">{current.caption}</p>
            )}
          </div>
        ) : (
          <p className="text-white/40 mt-20">No pages available</p>
        )}
      </div>

      {/* Page nav */}
      {pages.length > 1 && (
        <div className="relative flex items-center justify-center gap-4 py-3 border-t border-white/10">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="text-white/70 hover:text-white disabled:opacity-30 p-2 rounded"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex gap-1">
            {pages.map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`h-2 rounded-full transition-all ${i === page ? 'bg-white w-4' : 'bg-white/30 w-2'}`}
              />
            ))}
          </div>
          <button
            onClick={() => setPage(p => Math.min(pages.length - 1, p + 1))}
            disabled={page === pages.length - 1}
            className="text-white/70 hover:text-white disabled:opacity-30 p-2 rounded"
          >
            <ChevronRight size={20} />
          </button>
          <span className="text-white/50 text-xs absolute right-4">{page + 1} / {pages.length}</span>
        </div>
      )}
    </div>
  );
}

// ── Upload Modal ────────────────────────────────────────────────────────────
function UploadModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    title: '', subject: '', semester: 1, chapter: '', className: '',
    noteType: 'HANDWRITTEN', description: '', tags: '', date: '',
  });
  const [pages, setPages] = useState([{ imageUrl: '', caption: '' }]);
  const [uploadNote, { loading }] = useMutation(UPLOAD_NOTE, {
    refetchQueries: ['Notes'],
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addPage = () => setPages(p => [...p, { imageUrl: '', caption: '' }]);
  const removePage = (i) => setPages(p => p.filter((_, idx) => idx !== i));
  const setPageField = (i, k, v) => setPages(p => p.map((pg, idx) => idx === i ? { ...pg, [k]: v } : pg));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validPages = pages.filter(p => p.imageUrl.trim());
    if (!validPages.length) { alert('Add at least one image URL'); return; }
    try {
      await uploadNote({
        variables: {
          ...form,
          semester: parseInt(form.semester),
          tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
          pages: validPages.map((p, i) => ({
            imageUrl: p.imageUrl.trim(),
            pageNumber: i + 1,
            caption: p.caption || null,
          })),
        },
      });
      onSuccess();
      onClose();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <PenLine size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Upload Notes</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
              <input className="input" required value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Unit 3 – Sorting Algorithms" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subject *</label>
              <input className="input" required value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="e.g. Data Structures" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Semester</label>
              <select className="input" value={form.semester} onChange={e => set('semester', e.target.value)}>
                {SEMESTERS.map(s => <option key={s} value={s}>Semester {s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Chapter / Topic</label>
              <input className="input" value={form.chapter} onChange={e => set('chapter', e.target.value)} placeholder="e.g. Chapter 4" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Class / Batch</label>
              <input className="input" value={form.className} onChange={e => set('className', e.target.value)} placeholder="e.g. CSE-A, 2nd Year" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date of Notes</label>
              <input type="date" className="input" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
              <input className="input" value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="sorting, algorithms, exam" />
            </div>
          </div>

          {/* Note Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Note Type</label>
            <div className="flex flex-wrap gap-2">
              {NOTE_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set('noteType', t.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    form.noteType === t.value ? t.color + ' border-current' : 'text-gray-500 bg-gray-50 border-gray-200'
                  }`}
                >
                  <t.icon size={13} />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea className="input" rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of what's covered…" />
          </div>

          {/* Pages */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-700">Image Pages *</label>
              <span className="text-xs text-gray-400">Paste Google Drive / Imgur / any image URL</span>
            </div>
            <div className="space-y-3">
              {pages.map((pg, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-500 w-12">Page {i + 1}</span>
                    {pages.length > 1 && (
                      <button type="button" onClick={() => removePage(i)} className="ml-auto text-red-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <input
                    className="input text-sm mb-1.5"
                    value={pg.imageUrl}
                    onChange={e => setPageField(i, 'imageUrl', e.target.value)}
                    placeholder="https://drive.google.com/… or https://i.imgur.com/…"
                    required={i === 0}
                  />
                  <input
                    className="input text-xs"
                    value={pg.caption}
                    onChange={e => setPageField(i, 'caption', e.target.value)}
                    placeholder="Caption (optional)"
                  />
                  {pg.imageUrl && (
                    <div className="mt-2 h-20 rounded overflow-hidden bg-gray-200">
                      <img src={pg.imageUrl} alt="preview" className="w-full h-full object-cover object-top"
                        onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
              ))}
              <button type="button" onClick={addPage} className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium">
                <Plus size={14} /> Add Another Page
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? 'Uploading…' : 'Upload Notes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Note Card ───────────────────────────────────────────────────────────────
function NoteCard({ note, onView, onSave, onDelete, currentUserId }) {
  const typeInfo = noteTypeInfo(note.noteType);
  const TypeIcon = typeInfo.icon;

  return (
    <div className="card p-4 hover:shadow-md transition-shadow flex flex-col">
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
              <TypeIcon size={11} />
              {typeInfo.label}
            </span>
            <span className="text-xs text-gray-400">Sem {note.semester}</span>
            {note.pageCount > 1 && (
              <span className="text-xs text-gray-400">{note.pageCount} pg</span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">{note.title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{note.subject}{note.chapter ? ` • ${note.chapter}` : ''}</p>
        </div>
      </div>

      {/* Preview thumbnail */}
      {note.pages?.[0]?.imageUrl && (
        <div
          className="w-full h-32 rounded-lg overflow-hidden mb-3 bg-gray-100 cursor-pointer relative group flex-none"
          onClick={() => onView(note)}
        >
          <img
            src={note.pages[0].imageUrl}
            alt="Page 1"
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-200"
            onError={e => { e.target.src = 'https://placehold.co/400x200?text=Preview'; }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
            <Eye size={28} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          {note.pageCount > 1 && (
            <span className="absolute bottom-1.5 right-1.5 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
              +{note.pageCount - 1}
            </span>
          )}
        </div>
      )}

      {note.description && (
        <p className="text-xs text-gray-500 mb-2 line-clamp-2">{note.description}</p>
      )}

      {note.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {note.tags.slice(0, 3).map(t => (
            <span key={t} className="badge text-xs">{t}</span>
          ))}
          {note.tags.length > 3 && <span className="text-xs text-gray-400">+{note.tags.length - 3}</span>}
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-3 mt-auto">
        <img
          src={note.uploadedBy?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(note.uploadedBy?.name || 'U')}&size=24`}
          alt=""
          className="w-5 h-5 rounded-full"
        />
        <span className="text-xs text-gray-500 truncate">{note.uploadedBy?.name}</span>
        {note.className && <span className="text-xs text-gray-400 ml-auto flex-none">• {note.className}</span>}
      </div>

      <div className="flex items-center gap-2 border-t pt-3">
        <button
          onClick={() => onView(note)}
          className="btn-secondary text-xs flex-1 flex items-center justify-center gap-1 py-1.5"
        >
          <Eye size={13} /> View
        </button>
        <button
          onClick={() => onSave(note)}
          title={note.hasSaved ? 'Remove bookmark' : 'Bookmark'}
          className={`p-1.5 rounded-lg border transition-colors ${
            note.hasSaved ? 'text-yellow-600 bg-yellow-50 border-yellow-200' : 'text-gray-400 border-gray-200 hover:border-yellow-300 hover:text-yellow-500'
          }`}
        >
          {note.hasSaved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
        </button>
        <span className="text-xs text-gray-400 flex items-center gap-0.5">
          <Download size={11} /> {note.downloads}
        </span>
        <span className="text-xs text-gray-400 flex items-center gap-0.5">
          <Bookmark size={11} /> {note.savedCount}
        </span>
        {note.uploadedBy?.id === String(currentUserId) && (
          <button onClick={() => onDelete(note.id)} className="p-1.5 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 ml-auto">
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function NotesPage() {
  const { user } = useAuth();
  const [showUpload, setShowUpload] = useState(false);
  const [viewNote, setViewNote] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    subject: '', semester: '', chapter: '', noteType: '', search: '', savedOnly: false,
  });

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const vars = {
    subject:   filters.subject   || undefined,
    semester:  filters.semester  ? parseInt(filters.semester) : undefined,
    chapter:   filters.chapter   || undefined,
    noteType:  filters.noteType  || undefined,
    search:    filters.search    || undefined,
    savedOnly: filters.savedOnly || undefined,
  };

  const { data, loading, refetch } = useQuery(NOTES, { variables: vars, fetchPolicy: 'cache-and-network' });
  const [saveNote]    = useMutation(SAVE_NOTE,    { refetchQueries: ['Notes'] });
  const [deleteNote]  = useMutation(DELETE_NOTE,  { refetchQueries: ['Notes'] });

  const notes = data?.notes || [];
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v && k !== 'search' && k !== 'savedOnly').length
    + (filters.savedOnly ? 1 : 0);

  const handleSave   = (note)  => saveNote({ variables: { noteId: note.id } });
  const handleDelete = async (id) => {
    if (window.confirm('Delete this note?')) await deleteNote({ variables: { noteId: id } });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="text-blue-600" size={26} /> Notes Hub
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Upload &amp; discover handwritten notes, book extracts, previous papers &amp; more</p>
        </div>
        <button onClick={() => setShowUpload(true)} className="btn-primary flex items-center gap-2">
          <Upload size={16} /> Upload Notes
        </button>
      </div>

      {/* Search + filter bar */}
      <div className="card p-3 space-y-3">
        <div className="flex gap-2 flex-wrap">
          <div className="flex-1 min-w-48 relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input pl-9 py-2 text-sm"
              placeholder="Search notes, subjects, chapters…"
              value={filters.search}
              onChange={e => setF('search', e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`btn-secondary flex items-center gap-1.5 text-sm py-2 ${activeFilterCount > 0 ? 'border-blue-400 text-blue-600' : ''}`}
          >
            <SlidersHorizontal size={15} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-blue-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>
            )}
          </button>
          <button
            onClick={() => setF('savedOnly', !filters.savedOnly)}
            className={`btn-secondary flex items-center gap-1.5 text-sm py-2 ${filters.savedOnly ? 'text-yellow-600 border-yellow-400 bg-yellow-50' : ''}`}
          >
            <Bookmark size={15} /> Saved
          </button>
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-400'}`}>
              <LayoutGrid size={16} />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-400'}`}>
              <List size={16} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Subject</label>
              <input className="input text-sm py-1.5" value={filters.subject} onChange={e => setF('subject', e.target.value)} placeholder="e.g. Maths" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Semester</label>
              <select className="input text-sm py-1.5" value={filters.semester} onChange={e => setF('semester', e.target.value)}>
                <option value="">All Semesters</option>
                {SEMESTERS.map(s => <option key={s} value={s}>Sem {s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Chapter</label>
              <input className="input text-sm py-1.5" value={filters.chapter} onChange={e => setF('chapter', e.target.value)} placeholder="Chapter name" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Note Type</label>
              <select className="input text-sm py-1.5" value={filters.noteType} onChange={e => setF('noteType', e.target.value)}>
                <option value="">All Types</option>
                {NOTE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <button
              onClick={() => setFilters({ subject: '', semester: '', chapter: '', noteType: '', search: '', savedOnly: false })}
              className="text-xs text-blue-600 hover:underline col-span-full text-left"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Note type quick pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setF('noteType', '')}
          className={`flex-none px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
            !filters.noteType ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-500 border-gray-200 hover:border-gray-400'
          }`}
        >
          All
        </button>
        {NOTE_TYPES.map(t => (
          <button
            key={t.value}
            onClick={() => setF('noteType', filters.noteType === t.value ? '' : t.value)}
            className={`flex-none flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              filters.noteType === t.value ? t.color + ' border-current' : 'text-gray-500 border-gray-200 hover:border-gray-400'
            }`}
          >
            <t.icon size={12} />
            {t.label}
          </button>
        ))}
      </div>

      <p className="text-sm text-gray-400">
        {loading ? 'Loading…' : `${notes.length} note${notes.length !== 1 ? 's' : ''} found`}
      </p>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2 w-3/4" />
              <div className="h-32 bg-gray-100 rounded mb-3" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="card p-12 text-center">
          <BookOpen size={48} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No notes found</p>
          <p className="text-gray-400 text-sm mt-1">Be the first to share notes with your classmates!</p>
          <button onClick={() => setShowUpload(true)} className="btn-primary mt-4">Upload Notes</button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {notes.map(n => (
            <NoteCard
              key={n.id}
              note={n}
              currentUserId={user?.id}
              onView={setViewNote}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map(n => {
            const typeInfo = noteTypeInfo(n.noteType);
            const TypeIcon = typeInfo.icon;
            return (
              <div key={n.id} className="card p-3 flex gap-3 items-start hover:shadow-sm transition-shadow">
                {n.pages?.[0]?.imageUrl && (
                  <img
                    src={n.pages[0].imageUrl}
                    alt=""
                    className="w-16 h-16 rounded-lg object-cover object-top flex-none cursor-pointer"
                    onClick={() => setViewNote(n)}
                    onError={e => { e.target.src = 'https://placehold.co/64x64?text=N'; }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.color}`}>
                      <TypeIcon size={10} />{typeInfo.label}
                    </span>
                    <span className="text-xs text-gray-400">Sem {n.semester}</span>
                    {n.pageCount > 1 && <span className="text-xs text-gray-400">{n.pageCount} pages</span>}
                  </div>
                  <h3 className="font-semibold text-sm text-gray-900 truncate">{n.title}</h3>
                  <p className="text-xs text-gray-500">{n.subject}{n.chapter ? ` • ${n.chapter}` : ''}</p>
                  {n.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{n.description}</p>}
                </div>
                <div className="flex items-center gap-2 flex-none">
                  <button onClick={() => setViewNote(n)} className="btn-secondary text-xs py-1 px-2">View</button>
                  <button
                    onClick={() => handleSave(n)}
                    className={`p-1.5 rounded-lg border ${n.hasSaved ? 'text-yellow-600 bg-yellow-50 border-yellow-200' : 'text-gray-400 border-gray-200'}`}
                  >
                    {n.hasSaved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
                  </button>
                  {n.uploadedBy?.id === String(user?.id) && (
                    <button onClick={() => handleDelete(n.id)} className="p-1.5 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onSuccess={refetch} />}
      {viewNote && <Viewer note={viewNote} onClose={() => setViewNote(null)} />}
    </div>
  );
}
